import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import Checkbox from "components/UI/Checkbox";
import Input from 'components/UI/Input';
import EditorSection from 'view/EditorSection';
import { Main, Text } from 'styles/themeColors';
import Vendor from 'data/Vendor';
import Brand from 'data/Brand';
import { Color } from 'lib/styles';
import FeatureFlag from 'data/FeatureFlag';
import { useAuth } from 'lib/Auth';
import { USER_ROLE } from '../../constants/warehouseDeliveryOptions';
import { BRAND_CONSTANTS } from 'constants/brands';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';
import RadioList from 'components/UI/RadioList';
import { VENDOR_OPTIONS, VENDOR_OPTIONS_LABELS } from 'components/Vendor/AutomationSettings';
import { useAPI } from 'lib/API';
import { getBuyNowConfiguration } from 'requests/brand';
import BuyNowConfigModal from './BuyNowConfigModal';
import { Visibility, VisibilityOff, FileCopy } from '@mui/icons-material';
import { API_LEVEL } from 'constants/buyNow';
import { toast } from "components/UI/ToastNotification";

interface Props {
  brand: Brand;
  selectedVendor: Vendor;
  setBrand: (brand: Brand) => void;
  featureFlags: FeatureFlag;
  hasEditRights?: boolean;
  isSlbVendor?: boolean;
  setIsSlbVendor?: (isSlbVendor: boolean) => void;
  buyNowCredentials?: any;
  setBuyNowCredentials?: (creds: any) => void;
}

const Description = styled.p`
    margin: 0;
    font-size: 14px;
`;
const Heading = styled.h6`
  color: ${Color.black};
  font-weight: normal;
  margin-top:20px;
`;

const ConfigureButton = styled.div`
  font-size: 14px;
  color: ${Main.brandDark};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const FieldWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ActionButtonsContainer = styled.div`
  position: absolute;
  right: 10px;
  top: 72.5%;
  transform: translateY(-50%);
  display: flex;
  gap: 8px;
  align-items: center;
`;

const IconButton = styled.div`
  cursor: pointer;
  color: ${Main.brandDark};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  svg {
    font-size: 18px;
  }
`;

interface FieldActionButtonsProps {
  isVisible: boolean;
  onToggleVisibility: () => void;
  onCopy: () => void;
}

const FieldActionButtons: React.FC<FieldActionButtonsProps> = ({ isVisible, onToggleVisibility, onCopy }) => (
  <ActionButtonsContainer>
    <IconButton
      onClick={onToggleVisibility}
      title={isVisible ? 'Hide' : 'Show'}>
      {isVisible ? <VisibilityOff /> : <Visibility />}
    </IconButton>
    <IconButton onClick={onCopy} title="Copy to clipboard">
      <FileCopy />
    </IconButton>
  </ActionButtonsContainer>
);

const AutomationSettingTab: React.FC<Props> = ({
  brand,
  setBrand,
  selectedVendor,
  featureFlags,
  hasEditRights,
  isSlbVendor,
  setIsSlbVendor,
  buyNowCredentials,
  setBuyNowCredentials
}) => {
  const { user } = useAuth();
  const [selectedVendorOption, setSelectedVendorOption] = useState(selectedVendor.isSlbVendor ? VENDOR_OPTIONS.SLB_VENDOR : VENDOR_OPTIONS.NON_SLB_VENDOR);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showClientId, setShowClientId] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showClientUrl, setShowClientUrl] = useState(false);
  const [showBrandKey, setShowBrandKey] = useState(false);

  // validate user on the basis of role
  const hasValidUserRole = (user?.role === USER_ROLE.SuperAdmin || user?.role === USER_ROLE.VendorAdmin || user?.role === USER_ROLE.Admin);

  // API hook for fetching Buy Now configuration
  const buyNowConfigReq = useAPI({
    deferFn: getBuyNowConfiguration,
    onResolve: (result: any) => {
      if (result) {
        setBuyNowCredentials?.({
          clientId: result.clientId || '',
          clientSecret: result.clientSecret || '',
          clientUrl: result.clientUrl || '',
          buyNowConsumerKey: result.buyNowConsumerKey || '',
          brandCode: result.brandCode || '',
        });
      }
    },
    onReject: (err: any) => {
      console.error('Failed to fetch Buy Now configuration:', err);
    },
  });

  const onChangeMaterialApi = (isMaterialApiEnabled: boolean) => {

    if (!isMaterialApiEnabled) {
      setBrand({ ...brand, authenticationUrl: '', baseUrl: '', consumerKey: '', consumerSecret: '', isStockApiEnabled: false, isSalesOrderApiEnabled: false, isMaterialApiEnabled });
    }
    else {
      setBrand({ ...brand, isMaterialApiEnabled });
    }

  }
  const onChangeStockApi = (isStockApiEnabled: boolean) => {

    setBrand({ ...brand, isStockApiEnabled, isSalesOrderApiEnabled: isStockApiEnabled ? brand.isSalesOrderApiEnabled : false });

  }

  const onChangeSalesOrderApi = (isSalesOrderApiEnabled: boolean) => {
    setBrand({ ...brand, isSalesOrderApiEnabled });
  }

  const onChangeBuyNowApi = (isBuyNowEnabled: boolean) => {
    setBrand({ ...brand, isBuyNowEnabled });

    // Reset credentials and hide all fields when Buy Now is disabled
    if (!isBuyNowEnabled) {
      setBuyNowCredentials?.(null);
      setShowClientId(false);
      setShowClientSecret(false);
      setShowClientUrl(false);
      setShowBrandKey(false);
    }
  }

  const copyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${fieldName} copied to clipboard!`);
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  const getMaskedValue = (value: string) => {
    return '•'.repeat(value.length);
  };

  useEffect(() => {
    setIsSlbVendor?.(selectedVendorOption === VENDOR_OPTIONS.SLB_VENDOR);
  }, [selectedVendorOption]);

  // Reset buyNowCredentials when brand changes
  useEffect(() => {
    if (brand.isBuyNowEnabled) {
      buyNowConfigReq.run(brand.code);
    } else {
      setBuyNowCredentials?.(null);
    }
  }, [brand]);

  const isUserPermissionsEnabled = featureFlags?.businessUnit?.enabled;

  const isMaterialApiEnabled = selectedVendor?.isMaterialApiEnabled || isUserPermissionsEnabled;
  const isStocksApiEnabled = featureFlags?.stocks && featureFlags.stocks.enabled && selectedVendor.isStockApiEnabled || isUserPermissionsEnabled;
  const isNonSlbVendor = isSlbVendor === undefined ? !selectedVendor?.isSlbVendor : !isSlbVendor;
  const isSalesOrderApiEnabled = featureFlags?.salesOrder && featureFlags?.salesOrder.enabled && selectedVendor.isSalesApiEnabled || isUserPermissionsEnabled;

  // Permission for Add SEO Details
  const { readOnly: isAddDetailsReadOnly } = useReadPermission(PermissionCode.BRANDS_ADD_DETAILS);

  return (
    <FlexboxContainerComponent flexDirection="column" margin='0px 24px' gap='20px'>
      {
        setIsSlbVendor &&
        <div style={{ marginLeft: '-5px' }}>
          <ReadOnlyWrapper readOnly={!hasEditRights}>
            <RadioList
              options={VENDOR_OPTIONS_LABELS}
              selectedOption={selectedVendorOption}
              setSelectedOption={setSelectedVendorOption}
              color={Main.brandLight}
            />
          </ReadOnlyWrapper>
        </div>
      }

      {isMaterialApiEnabled &&
        <FlexboxContainerComponent flexDirection="column">
          <ReadOnlyWrapper readOnly={!hasEditRights}>
            <Checkbox
              id="material-api-check"
              name="isMaterialApiEnabled"
              label="Material API"
              appearance="none"
              checkboxSize="s"
              checked={brand.isMaterialApiEnabled}
              onChange={onChangeMaterialApi}
              theme={'brandLight'}
              bgColor={Main.brandLight}
              disabled={!hasEditRights}
              permissionCode={PermissionCode.AUTOMATION_SETTINGS}
            />
            <Description>
              {BRAND_CONSTANTS.TRACK_PRODUCTS_API}
            </Description>
          </ReadOnlyWrapper>
          {isStocksApiEnabled ?
            <div style={{ marginTop: 20 }}>
              <ReadOnlyWrapper readOnly={!hasEditRights}>
                <Checkbox
                  id="stock-api-check"
                  name="isStockApiEnabled"
                  disabled={!brand.isMaterialApiEnabled || !hasEditRights}
                  label="Stock API"
                  appearance="none"
                  checkboxSize="s"
                  checked={brand.isStockApiEnabled}
                  onChange={onChangeStockApi}
                  theme={'brandLight'}
                  bgColor={Main.brandLight}
                  permissionCode={PermissionCode.AUTOMATION_SETTINGS}
                />
                <Description>
                  {BRAND_CONSTANTS.AUTOMATE_INVENTORY_API}
                </Description>
              </ReadOnlyWrapper>
            </div> : null}

          {isNonSlbVendor && (
            <ReadOnlyWrapper readOnly={!hasEditRights}>
              <Heading>Authentication Setting</Heading>
              <EditorSection noBorder={true}>
                <EditorSection countType="item" noBorder={true}>
                  <FlexboxContainerComponent justifyContent="space-between">
                    <Input
                      id='client-id'
                      labelName="Client id"
                      placeholder="123456"
                      disabled={!brand.isMaterialApiEnabled || !hasEditRights}
                      displayAsterisk={true}
                      width="40%"
                      value={brand.consumerKey}
                      onInputChange={(consumerKey) => setBrand({ ...brand, consumerKey })}
                      readOnly={isAddDetailsReadOnly}

                    />
                    <div style={{ width: '30px' }} />
                    <Input
                      id='client-secret'
                      labelName="Client Secret"
                      placeholder="fgi1@ys9"
                      disabled={!brand.isMaterialApiEnabled || !hasEditRights}
                      displayAsterisk={true}
                      width="40%"
                      value={brand.consumerSecret}
                      onInputChange={(consumerSecret) => setBrand({ ...brand, consumerSecret })}
                      readOnly={isAddDetailsReadOnly}
                    />
                    <div style={{ width: '30px' }} />
                    <Input
                      id='authentication-url'
                      labelName="Authentication URL"
                      placeholder="https://"
                      disabled={!brand.isMaterialApiEnabled || !hasEditRights}
                      displayAsterisk={true}
                      width="40%"
                      value={brand.authenticationUrl}
                      onInputChange={(authenticationUrl) => setBrand({ ...brand, authenticationUrl })}
                      readOnly={isAddDetailsReadOnly}
                    />
                  </FlexboxContainerComponent>
                </EditorSection>

                <EditorSection countType="item" noBorder={true}>
                  <FlexboxContainerComponent justifyContent="space-between">
                    <Input
                      id='base-url'
                      labelName="Base URL"
                      placeholder="https://"
                      disabled={!brand.isMaterialApiEnabled || !hasEditRights}
                      displayAsterisk={true}
                      width="32%"
                      value={brand.baseUrl}
                      onInputChange={(baseUrl) => setBrand({ ...brand, baseUrl })}
                      readOnly={isAddDetailsReadOnly}
                    />
                  </FlexboxContainerComponent>
                </EditorSection>
              </EditorSection>
            </ReadOnlyWrapper>
          )}
        </FlexboxContainerComponent>}

      {
        hasValidUserRole && isSalesOrderApiEnabled &&
        <ReadOnlyWrapper readOnly={!hasEditRights}>
          <Checkbox
            id="saleorder-api-check"
            name="isSalesApiEnabled"
            // disabled={disabled}
            label="Sales Order API"
            appearance="none"
            checkboxSize="s"
            checked={brand.isSalesOrderApiEnabled}
            onChange={onChangeSalesOrderApi}
            theme={'brandLight'}
            bgColor={Main.brandLight}
            disabled={!brand.isMaterialApiEnabled || !brand.isStockApiEnabled || !hasEditRights}
            permissionCode={PermissionCode.AUTOMATION_SETTINGS}
          />
          <Description style={{ color: (!brand.isMaterialApiEnabled || !brand.isStockApiEnabled) ? Text.disabled : Text.primary }}>
            This API helps you automate sales order creation. Requires both Material API and Stock API to be enabled first. However, if you prefer, you can still revert to the manual process by deselecting the same checkbox.
          </Description>
        </ReadOnlyWrapper>
      }

      {
        isUserPermissionsEnabled &&
        <div style={{ marginBottom: 20 }}>
          <ReadOnlyWrapper readOnly={!hasEditRights}>
            <Checkbox
              id="buyNow-api-check"
              name="isBuyNowApiEnabled"
              label="Buy Now API"
              appearance="none"
              checkboxSize="s"
              checked={brand.isBuyNowEnabled}
              onChange={onChangeBuyNowApi}
              theme={'brandLight'}
              bgColor={Main.brandLight}
              disabled={!hasEditRights}
              permissionCode={PermissionCode.AUTOMATION_SETTINGS}
            />
            <Description style={{ color: Text.primary }}>
              Enable a Buy Now button on your own website for products listed on OFMP. When customers click the button, they'll be redirected to OFMP to complete their purchase through our secure checkout.
            </Description>
          </ReadOnlyWrapper>
          <ConfigureButton onClick={() => setShowConfigModal(true)}>How to Configure</ConfigureButton>
          {
            brand.isBuyNowEnabled && buyNowCredentials &&
            <FlexboxContainerComponent flexDirection='column' gap='20px' width='950px' margin='20px 0px 0px 0px'>
              {/* Row 1: Client ID and Client Secret */}
              <FlexboxContainerComponent gap='20px'>
                <FieldWrapper>
                  <Input
                    id="buy-now-client-id"
                    labelName="Client ID"
                    disabled
                    width="100%"
                    value={showClientId ? (buyNowCredentials?.clientId || '') : getMaskedValue(buyNowCredentials?.clientId || '')}
                    placeholder="Client ID"
                  />
                  <FieldActionButtons
                    isVisible={showClientId}
                    onToggleVisibility={() => setShowClientId(!showClientId)}
                    onCopy={() => copyToClipboard(buyNowCredentials?.clientId || '', 'Client ID')}
                  />
                </FieldWrapper>

                <FieldWrapper>
                  <Input
                    id="buy-now-client-secret"
                    labelName="Client Secret"
                    disabled
                    width="100%"
                    value={showClientSecret ? (buyNowCredentials?.clientSecret || '') : getMaskedValue(buyNowCredentials?.clientSecret || '')}
                    displayAsterisk={false}
                    placeholder="Client Secret"
                  />
                  <FieldActionButtons
                    isVisible={showClientSecret}
                    onToggleVisibility={() => setShowClientSecret(!showClientSecret)}
                    onCopy={() => copyToClipboard(buyNowCredentials?.clientSecret || '', 'Client Secret')}
                  />
                </FieldWrapper>
              </FlexboxContainerComponent>

              {/* Row 2: Client URL and Brand Key */}
              <FlexboxContainerComponent gap='20px'>
                <FieldWrapper>
                  <Input
                    id="buy-now-client-url"
                    labelName="Client URL"
                    disabled
                    width="100%"
                    value={showClientUrl ? (buyNowCredentials?.clientUrl || '') : getMaskedValue(buyNowCredentials?.clientUrl || '')}
                    displayAsterisk={false}
                    placeholder="Client URL"
                  />
                  <FieldActionButtons
                    isVisible={showClientUrl}
                    onToggleVisibility={() => setShowClientUrl(!showClientUrl)}
                    onCopy={() => copyToClipboard(buyNowCredentials?.clientUrl || '', 'Client URL')}
                  />
                </FieldWrapper>

                <FieldWrapper>
                  <Input
                    id="buy-now-brand-key"
                    labelName="Brand Key"
                    disabled
                    width="100%"
                    value={showBrandKey ? (buyNowCredentials?.buyNowConsumerKey || '') : getMaskedValue(buyNowCredentials?.buyNowConsumerKey || '')}
                    displayAsterisk={false}
                    placeholder="Brand Key"
                  />
                  <FieldActionButtons
                    isVisible={showBrandKey}
                    onToggleVisibility={() => setShowBrandKey(!showBrandKey)}
                    onCopy={() => copyToClipboard(buyNowCredentials?.buyNowConsumerKey || '', 'Brand Key')}
                  />
                </FieldWrapper>
              </FlexboxContainerComponent>
            </FlexboxContainerComponent>
          }

          <BuyNowConfigModal
            show={showConfigModal}
            onClose={() => setShowConfigModal(false)}
            level={API_LEVEL.BRAND}
            credentials={buyNowCredentials}
          />
        </div>
      }
    </FlexboxContainerComponent >
  );
};

export default AutomationSettingTab;
